/**
 * Global usage setup — make a VortEX instance reachable from ANY folder, not
 * just the instance directory. The lever is the user's GLOBAL Claude config
 * (`~/.claude/`), which Claude Code reads in every project:
 *
 *   1. `~/.claude/settings.json`     — global SessionStart/SessionEnd hooks, so
 *      the boot report + worklog-net fire in every folder. SAME command string
 *      as the per-instance hook, so Claude Code's identical-hook dedup means the
 *      instance folder never double-fires.
 *   2. `~/.claude/vortex-global.json` — a small pointer file recording the
 *      instance path (and a decline marker). `resolveRepoRoot()` reads
 *      `instanceRoot` so commands run from any folder record to the one instance
 *      WITHOUT needing an env var baked into the hook command (which would be
 *      shell-dependent and would break hook dedup).
 *   3. `~/.claude/CLAUDE.md`         — a small, marker-delimited pointer block
 *      telling the agent where the instance is and to read its `AI-RULES.md` for
 *      VortEX work. Regenerated in place; the user's other CLAUDE.md content is
 *      preserved.
 *
 * Every write is merge-safe and idempotent. Pure helpers (block upsert, state
 * read) are separated from the thin fs wrappers so the merge is unit-testable.
 * All functions take an explicit `home` (default `os.homedir()`) so tests can
 * point at a fake home without touching the real `~/.claude`.
 */

import { homedir } from "node:os";
import { existsSync, readFileSync } from "node:fs";
import { mkdir, readFile, writeFile } from "node:fs/promises";
import { isAbsolute, join } from "node:path";
import {
  ensureVortexHooks,
  parseSettings,
  serializeSettings,
  SESSION_START_COMMAND,
  SESSION_END_COMMAND,
  type ClaudeSettings,
} from "./ensure-hooks.js";

/**
 * Read a file's text, or null ONLY when it genuinely does not exist (ENOENT).
 * Any other error (permission, lock, transient IO, a directory in its place) is
 * rethrown — treating an unreadable-but-present file as "missing" would let a
 * later write clobber the user's real content.
 */
async function readFileIfExists(path: string): Promise<string | null> {
  try {
    return await readFile(path, "utf8");
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return null;
    throw e;
  }
}

/** True when a path is safe to embed/record: absolute, an instance, no control chars/backticks. */
export function isSafeInstanceRoot(dir: string): boolean {
  return (
    typeof dir === "string" &&
    dir.trim().length > 0 &&
    isAbsolute(dir) &&
    !/[\r\n`]/.test(dir) &&
    isInstanceRoot(dir)
  );
}

export function globalClaudeDir(home: string = homedir()): string {
  return join(home, ".claude");
}
export function globalSettingsPath(home: string = homedir()): string {
  return join(globalClaudeDir(home), "settings.json");
}
/** The pointer/state file: `{ instanceRoot?, declinedAt? }`. */
export function globalStatePath(home: string = homedir()): string {
  return join(globalClaudeDir(home), "vortex-global.json");
}
export function globalMemoryPath(home: string = homedir()): string {
  return join(globalClaudeDir(home), "CLAUDE.md");
}

interface GlobalState {
  instanceRoot?: string;
  declinedAt?: string;
}

/** Read `vortex-global.json` leniently — any error or non-object → null. */
function readGlobalStateRaw(home: string = homedir()): GlobalState | null {
  try {
    const parsed: unknown = JSON.parse(readFileSync(globalStatePath(home), "utf8"));
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as GlobalState;
    }
  } catch {
    // missing / unreadable / malformed → treated as "no state"
  }
  return null;
}

/**
 * The instance root recorded by a prior `vortex global-setup`, or null. Read by
 * `resolveRepoRoot()` so any-folder commands target the central instance.
 */
export function readGlobalInstancePointer(home: string = homedir()): string | null {
  const root = readGlobalStateRaw(home)?.instanceRoot;
  return typeof root === "string" && root.trim().length > 0 ? root.trim() : null;
}

/**
 * Does `dir` look like an initialized VortEX instance? Used by
 * `resolveRepoRoot()` so that running INSIDE any instance uses that instance,
 * and only falls back to the global pointer elsewhere.
 */
export function isInstanceRoot(dir: string): boolean {
  return (
    existsSync(join(dir, ".agent", "vortex.json")) ||
    existsSync(join(dir, "data", "_memory", "user_profile.md"))
  );
}

/** True if the global settings.json already carries BOTH our session hooks. */
export function globalSettingsHasHook(home: string = homedir()): boolean {
  try {
    const settings = parseSettings(readFileSync(globalSettingsPath(home), "utf8"));
    const wired = (event: "SessionStart" | "SessionEnd", command: string): boolean =>
      Boolean(settings.hooks?.[event]?.some((g) => g.hooks?.some((h) => h.command === command)));
    return wired("SessionStart", SESSION_START_COMMAND) && wired("SessionEnd", SESSION_END_COMMAND);
  } catch {
    return false;
  }
}

/**
 * State of global usage on this machine. `done` = pointer set AND global hook
 * wired (and, if `instanceRoot` given, the pointer matches it). `declined` = the
 * user dismissed the offer. The session-start offer shows only when neither.
 */
export function inspectGlobalSetup(
  home: string = homedir(),
  instanceRoot?: string,
): { readonly done: boolean; readonly declined: boolean } {
  const state = readGlobalStateRaw(home) ?? {};
  const ptr =
    typeof state.instanceRoot === "string" && state.instanceRoot.trim().length > 0
      ? state.instanceRoot.trim()
      : null;
  const hooked = globalSettingsHasHook(home);
  const done = Boolean(ptr) && hooked && (!instanceRoot || ptr === instanceRoot);
  return { done, declined: typeof state.declinedAt === "string" };
}

// --- CLAUDE.md managed block (pure, testable) ---

// Distinctive, namespaced markers anchored to whole lines (m flag, ^…$), so an
// ordinary user comment cannot be mistaken for the managed block and clobbered.
// The block is replaced only between a BEGIN line and the next END line; callers
// MUST pass a control-char-free instanceRoot (see isSafeInstanceRoot) so the
// value can't inject a premature END marker.
const BLOCK_BEGIN =
  "<!-- VORTEX-GLOBAL:BEGIN — managed by `vortex global-setup`; edit OUTSIDE these markers (this block is regenerated) -->";
const BLOCK_END = "<!-- VORTEX-GLOBAL:END -->";
// CRLF-tolerant: a global CLAUDE.md may have \r\n line endings (common on
// Windows, or after an editor/git normalization), so accept \r?\n around the
// markers and an optional trailing \r before the line end — otherwise a managed
// block converted to CRLF would fail to match and a second block would be
// appended on the next run.
const BLOCK_RE =
  /^<!-- VORTEX-GLOBAL:BEGIN\b[^\r\n]*-->[ \t]*\r?\n[\s\S]*?\r?\n<!-- VORTEX-GLOBAL:END -->[ \t]*\r?$/m;

/** The marker-delimited pointer block written into the global CLAUDE.md. */
export function renderGlobalBlock(instanceRoot: string): string {
  return [
    BLOCK_BEGIN,
    "## VortEX (always-on)",
    "",
    `A VortEX instance lives at: \`${instanceRoot}\``,
    "",
    "When doing VortEX work (worklog, decisions, recall, memory) — or any substantive coding you want VortEX's working rules for — read that instance's `AI-RULES.md` and follow it. Run `vortex` commands so they record to that instance (they resolve it automatically; or pass `VORTEX_REPO_ROOT=<instance path>`). Stay in the current folder — do not `cd` into the instance.",
    BLOCK_END,
  ].join("\n");
}

/**
 * Insert or replace the VortEX block in an existing CLAUDE.md, preserving all
 * other content. Idempotent: a second call with the same instanceRoot returns
 * identical text.
 */
export function upsertGlobalBlock(existing: string, instanceRoot: string): string {
  const block = renderGlobalBlock(instanceRoot);
  if (BLOCK_RE.test(existing)) {
    return existing.replace(BLOCK_RE, block);
  }
  const head = existing.replace(/\s*$/, "");
  return (head.length > 0 ? head + "\n\n" : "") + block + "\n";
}

// --- fs writers (thin) ---

export interface GlobalSetupWrite {
  readonly created: readonly string[];
  readonly modified: readonly string[];
  readonly skipped: readonly string[];
}

/**
 * Apply global usage setup for `instanceRoot`: merge global hooks, write the
 * pointer, and upsert the CLAUDE.md block. Non-destructive and idempotent —
 * re-running with the same instance is a no-op (everything reported `skipped`).
 */
export async function applyGlobalSetup(opts: {
  readonly instanceRoot: string;
  readonly home?: string;
}): Promise<GlobalSetupWrite> {
  const home = opts.home ?? homedir();
  const instanceRoot = opts.instanceRoot;
  const created: string[] = [];
  const modified: string[] = [];
  const skipped: string[] = [];

  const statePath = globalStatePath(home);
  const settingsPath = globalSettingsPath(home);
  const instSettingsPath = join(instanceRoot, ".claude", "settings.json");
  const mdPath = globalMemoryPath(home);

  // --- PREFLIGHT: read + parse everything FIRST. The throw-prone step is
  // parseSettings (malformed JSON). Doing all parses before any write means a
  // malformed global OR instance settings.json aborts here, leaving nothing
  // half-written (no "pointer+hook set but CLAUDE.md missing" inconsistency).
  // Reads use readFileIfExists so an unreadable-but-present file is NOT mistaken
  // for missing and overwritten.
  const hadState = existsSync(statePath);
  const prevState = readGlobalStateRaw(home) ?? {};
  const settingsText = await readFileIfExists(settingsPath);
  const mergedGlobal = ensureVortexHooks(parseSettings(settingsText));
  const instText = await readFileIfExists(instSettingsPath);
  const mergedInst = instText !== null ? ensureVortexHooks(parseSettings(instText)) : null;
  const mdRead = await readFileIfExists(mdPath);
  const mdText = mdRead ?? "";
  const nextMd = upsertGlobalBlock(mdText, instanceRoot);

  // --- WRITE. Pointer first: if a later disk write fails, the worst surviving
  // state is "pointer set, hook/block not yet written" — benign (commands still
  // resolve to the right instance; the hook just isn't active). The reverse could
  // leave "hook active, no pointer", which would mis-resolve to cwd.
  await mkdir(globalClaudeDir(home), { recursive: true });

  // 1) pointer file (set instanceRoot; clear a stale decline marker; keep others)
  const { declinedAt: _wasDeclined, ...restState } = prevState;
  const nextState: GlobalState = { ...restState, instanceRoot };
  if (!hadState || prevState.instanceRoot !== instanceRoot || typeof prevState.declinedAt === "string") {
    await writeFile(statePath, JSON.stringify(nextState, null, 2) + "\n", "utf8");
    (hadState ? modified : created).push(statePath);
  } else {
    skipped.push(statePath);
  }

  // 2) global hooks (byte-identical to the per-instance hook → Claude Code dedups)
  if (!mergedGlobal.alreadyWired) {
    await writeFile(settingsPath, serializeSettings(mergedGlobal.settings), "utf8");
    (settingsText === null ? created : modified).push(settingsPath);
  } else {
    skipped.push(settingsPath);
  }

  // 3) migrate the instance's OWN hooks to the same command (so per-instance and
  //    global stay identical and dedup). Only when the instance already has a
  //    settings file — if it has none, the global hook covers the instance folder
  //    on its own, so we don't create one.
  if (mergedInst !== null) {
    if (!mergedInst.alreadyWired) {
      await writeFile(instSettingsPath, serializeSettings(mergedInst.settings), "utf8");
      modified.push(instSettingsPath);
    } else {
      skipped.push(instSettingsPath);
    }
  }

  // 4) CLAUDE.md pointer block (regenerate in place; preserve other content)
  if (nextMd !== mdText) {
    await writeFile(mdPath, nextMd, "utf8");
    (mdRead === null ? created : modified).push(mdPath);
  } else {
    skipped.push(mdPath);
  }

  return { created, modified, skipped };
}

/**
 * Record that the user declined the global-usage offer, so the session-start
 * report stops offering it. Preserves any existing pointer. Returns the path
 * written.
 */
export async function recordGlobalSetupDecline(opts?: {
  readonly home?: string;
  readonly now?: Date;
}): Promise<string> {
  const home = opts?.home ?? homedir();
  const now = opts?.now ?? new Date();
  await mkdir(globalClaudeDir(home), { recursive: true });
  const statePath = globalStatePath(home);
  const prevState = readGlobalStateRaw(home) ?? {};
  const nextState: GlobalState = { ...prevState, declinedAt: now.toISOString() };
  await writeFile(statePath, JSON.stringify(nextState, null, 2) + "\n", "utf8");
  return statePath;
}

export type { ClaudeSettings };
